import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, KeyRound, MapPin, UserPlus, ChevronDown } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API, FORMAT_TYPES, CLIENT_STATUSES, formatApiErrorDetail } from "../../lib/operatorWalk";

const inputCls = "w-full border border-brand-200 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-500";
const blankLocation = () => ({ name: "", address: "", concept_name: "", format_type: "full-service", visit_day: "", visit_window: "" });

function CreateClientForm({ onCreated, authHeader }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_email: "", billing_email: "", phone: "", status: "active", is_demo: false, login_email: "", login_password: "", contact_name: "" });
  const [locations, setLocations] = useState([blankLocation()]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setLoc = (i, k, v) => setLocations((p) => p.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const submit = async () => {
    if (!form.company_name.trim() || !form.contact_email.trim()) {
      toast.error("Company name and contact email are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        billing_email: form.billing_email || null,
        login_email: form.login_email || null,
        login_password: form.login_password || null,
        locations: locations.filter((l) => l.name.trim()),
      };
      await axios.post(`${OW_API}/admin/ow/clients`, payload, { headers: authHeader });
      toast.success("Client created.");
      setForm({ company_name: "", contact_email: "", billing_email: "", phone: "", status: "active", is_demo: false, login_email: "", login_password: "", contact_name: "" });
      setLocations([blankLocation()]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create client.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-brand-200 bg-white" data-testid="create-client">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        data-testid="create-client-toggle"
      >
        <span className="font-serif text-xl text-brand-700 inline-flex items-center gap-2">
          <Plus className="w-5 h-5" /> New client
        </span>
        <ChevronDown className={`w-5 h-5 text-brand-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-6 space-y-4 border-t border-brand-100 pt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Company name" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="client-company" />
            <input className={inputCls} placeholder="Primary contact email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="client-contact-email" />
            <input className={inputCls} placeholder="Billing email (optional)" value={form.billing_email} onChange={(e) => set("billing_email", e.target.value)} data-testid="client-billing-email" />
            <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="client-phone" />
            <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="client-status">
              {CLIENT_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-ink-soft px-1" data-testid="client-demo-toggle">
              <input type="checkbox" checked={form.is_demo} onChange={(e) => set("is_demo", e.target.checked)} className="w-4 h-4 accent-brand-700" data-testid="client-is-demo" />
              Demo account (excluded from metrics)
            </label>
          </div>

          <div className="text-xs uppercase tracking-[0.18em] text-ink-muted pt-2">Locations</div>
          {locations.map((l, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-brand-100 p-3" data-testid={`new-location-${i}`}>
              <input className={inputCls} placeholder="Location name" value={l.name} onChange={(e) => setLoc(i, "name", e.target.value)} data-testid={`new-loc-name-${i}`} />
              <input className={inputCls} placeholder="Address" value={l.address} onChange={(e) => setLoc(i, "address", e.target.value)} />
              <input className={inputCls} placeholder="Concept name" value={l.concept_name} onChange={(e) => setLoc(i, "concept_name", e.target.value)} />
              <select className={inputCls} value={l.format_type} onChange={(e) => setLoc(i, "format_type", e.target.value)}>
                {FORMAT_TYPES.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <input className={inputCls} placeholder="Standing visit day (e.g. Second Friday)" value={l.visit_day} onChange={(e) => setLoc(i, "visit_day", e.target.value)} />
              <input className={inputCls} placeholder="Visit time window (e.g. Dinner service)" value={l.visit_window} onChange={(e) => setLoc(i, "visit_window", e.target.value)} />
            </div>
          ))}
          <button onClick={() => setLocations((p) => [...p, blankLocation()])} className="text-sm text-brand-700 inline-flex items-center gap-1.5" data-testid="add-location-row">
            <Plus className="w-4 h-4" /> Add another location
          </button>

          <div className="text-xs uppercase tracking-[0.18em] text-ink-muted pt-2">Client login (optional)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input className={inputCls} placeholder="Login email" value={form.login_email} onChange={(e) => set("login_email", e.target.value)} data-testid="client-login-email" />
            <input className={inputCls} placeholder="Temp password (min 8)" value={form.login_password} onChange={(e) => set("login_password", e.target.value)} data-testid="client-login-password" />
            <input className={inputCls} placeholder="Contact name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
          </div>

          <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60" data-testid="create-client-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create client
          </button>
        </div>
      )}
    </div>
  );
}

function ClientCard({ client, onChanged, authHeader }) {
  const [busy, setBusy] = useState(false);
  const [addingLoc, setAddingLoc] = useState(false);
  const [newLoc, setNewLoc] = useState(blankLocation());
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "" });
  const [showAddUser, setShowAddUser] = useState(false);

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await axios.patch(`${OW_API}/admin/ow/clients/${client.id}`, { status }, { headers: authHeader });
      toast.success("Status updated.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteClient = async () => {
    if (!window.confirm(`Delete ${client.company_name} and all its reports? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${OW_API}/admin/ow/clients/${client.id}`, { headers: authHeader });
      toast.success("Client deleted.");
      onChanged?.();
    } catch {
      toast.error("Could not delete client.");
    } finally {
      setBusy(false);
    }
  };

  const addLocation = async () => {
    if (!newLoc.name.trim()) return toast.error("Location name is required.");
    setBusy(true);
    try {
      await axios.post(`${OW_API}/admin/ow/clients/${client.id}/locations`, newLoc, { headers: authHeader });
      toast.success("Location added.");
      setNewLoc(blankLocation());
      setAddingLoc(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createUser = async () => {
    if (!newUser.email || newUser.password.length < 8) return toast.error("Email and an 8+ character password are required.");
    setBusy(true);
    try {
      await axios.post(`${OW_API}/admin/ow/clients/${client.id}/user`, newUser, { headers: authHeader });
      toast.success("Login created.");
      setNewUser({ email: "", password: "", name: "" });
      setShowAddUser(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (userId) => {
    if (resetPw.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await axios.post(`${OW_API}/admin/ow/users/${userId}/reset-password`, { password: resetPw }, { headers: authHeader });
      toast.success("Password reset.");
      setResetFor(null);
      setResetPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-brand-200 bg-white" data-testid={`client-card-${client.id}`}>
      <div className="px-5 py-4 flex items-start justify-between gap-4 border-b border-brand-100">
        <div>
          <div className="font-serif text-2xl text-brand-700 flex items-center gap-3">
            {client.company_name}
            {client.is_demo && (
              <span className="text-[10px] uppercase tracking-[0.18em] bg-brand-200 text-brand-700 px-2 py-1" data-testid={`client-demo-badge-${client.id}`}>
                Demo
              </span>
            )}
          </div>
          <div className="text-sm text-ink-muted mt-1">{client.contact_email}{client.phone ? ` . ${client.phone}` : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={client.status}
            onChange={(e) => updateStatus(e.target.value)}
            disabled={busy}
            className="border border-brand-200 px-2 py-1.5 text-sm"
            data-testid={`client-status-${client.id}`}
          >
            {CLIENT_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button onClick={deleteClient} disabled={busy} className="p-2 text-red-600 hover:bg-red-50" data-testid={`client-delete-${client.id}`} title="Delete client">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Locations */}
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-ink-muted mb-3">Locations</div>
          <ul className="space-y-2">
            {(client.locations || []).map((l) => (
              <li key={l.id} className="flex items-start gap-2 text-sm text-ink-soft" data-testid={`client-location-${l.id}`}>
                <MapPin className="w-4 h-4 text-brand-400 mt-0.5 shrink-0" />
                <span>
                  <span className="font-medium text-brand-700">{l.name}</span>
                  <span className="text-ink-muted"> . {l.format_type}</span>
                  {l.visit_day && <span className="text-ink-muted"> . {l.visit_day}, {l.visit_window}</span>}
                </span>
              </li>
            ))}
            {(client.locations || []).length === 0 && <li className="text-sm text-ink-muted">No locations.</li>}
          </ul>
          {addingLoc ? (
            <div className="mt-3 space-y-2 border border-brand-100 p-3">
              <input className={inputCls} placeholder="Location name" value={newLoc.name} onChange={(e) => setNewLoc((p) => ({ ...p, name: e.target.value }))} data-testid={`add-loc-name-${client.id}`} />
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="Address" value={newLoc.address} onChange={(e) => setNewLoc((p) => ({ ...p, address: e.target.value }))} />
                <select className={inputCls} value={newLoc.format_type} onChange={(e) => setNewLoc((p) => ({ ...p, format_type: e.target.value }))}>
                  {FORMAT_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <input className={inputCls} placeholder="Visit day" value={newLoc.visit_day} onChange={(e) => setNewLoc((p) => ({ ...p, visit_day: e.target.value }))} />
                <input className={inputCls} placeholder="Visit window" value={newLoc.visit_window} onChange={(e) => setNewLoc((p) => ({ ...p, visit_window: e.target.value }))} />
              </div>
              <div className="flex gap-2">
                <button onClick={addLocation} disabled={busy} className="bg-brand-700 text-white px-4 py-2 text-sm" data-testid={`save-loc-${client.id}`}>Save</button>
                <button onClick={() => setAddingLoc(false)} className="px-4 py-2 text-sm text-ink-muted">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddingLoc(true)} className="mt-3 text-sm text-brand-700 inline-flex items-center gap-1.5" data-testid={`add-location-${client.id}`}>
              <Plus className="w-4 h-4" /> Add location
            </button>
          )}
        </div>

        {/* Logins */}
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-ink-muted mb-3">Client logins</div>
          <ul className="space-y-2">
            {(client.users || []).map((u) => (
              <li key={u.id} className="text-sm" data-testid={`client-user-${u.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-ink-soft">{u.email}</span>
                  <button onClick={() => setResetFor(resetFor === u.id ? null : u.id)} className="text-brand-700 inline-flex items-center gap-1 text-xs" data-testid={`reset-pw-${u.id}`}>
                    <KeyRound className="w-3.5 h-3.5" /> Reset
                  </button>
                </div>
                {resetFor === u.id && (
                  <div className="flex gap-2 mt-2">
                    <input className={inputCls} placeholder="New password (min 8)" value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid={`reset-pw-input-${u.id}`} />
                    <button onClick={() => resetPassword(u.id)} disabled={busy} className="bg-brand-700 text-white px-3 py-2 text-sm" data-testid={`reset-pw-save-${u.id}`}>Set</button>
                  </div>
                )}
              </li>
            ))}
            {(client.users || []).length === 0 && <li className="text-sm text-ink-muted">No login yet.</li>}
          </ul>
          {showAddUser ? (
            <div className="mt-3 space-y-2 border border-brand-100 p-3">
              <input className={inputCls} placeholder="Login email" value={newUser.email} onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))} data-testid={`new-user-email-${client.id}`} />
              <input className={inputCls} placeholder="Temp password (min 8)" value={newUser.password} onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))} data-testid={`new-user-password-${client.id}`} />
              <div className="flex gap-2">
                <button onClick={createUser} disabled={busy} className="bg-brand-700 text-white px-4 py-2 text-sm" data-testid={`new-user-save-${client.id}`}>Create login</button>
                <button onClick={() => setShowAddUser(false)} className="px-4 py-2 text-sm text-ink-muted">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddUser(true)} className="mt-3 text-sm text-brand-700 inline-flex items-center gap-1.5" data-testid={`add-user-${client.id}`}>
              <UserPlus className="w-4 h-4" /> Add login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminClients({ clients, onChanged }) {
  const { authHeader } = usePortalAuth();
  return (
    <div className="space-y-5" data-testid="admin-clients">
      <CreateClientForm onCreated={onChanged} authHeader={authHeader} />
      {clients.map((c) => (
        <ClientCard key={c.id} client={c} onChanged={onChanged} authHeader={authHeader} />
      ))}
      {clients.length === 0 && <p className="text-ink-muted text-sm">No clients yet. Create your first client above.</p>}
    </div>
  );
}
